import { Time } from "@internationalized/date";
import type { Nullable } from "@msuite/utils/types";
import { DateInput, DateSegment, I18nProvider, TimeField } from "react-aria-components";
import { type Control, Controller, type FieldValues, type Path } from "react-hook-form";
import { InputWrapper } from "../form/input-wrapper";
import { cn } from "../utils";
import { getPlaceholderString } from "./shared";

interface TimeInputProps<T extends FieldValues> {
	control: Control<T>;
	name: Path<T>;
	label: Nullable<string>;
	isRequired?: boolean;
	granularity?: "minute" | "second";
}

export function TimeInput<T extends FieldValues>({
	name,
	control,
	label,
	isRequired,
	granularity = "minute",
}: TimeInputProps<T>) {
	/** Render */
	return (
		<InputWrapper
			label={label}
			isRequired={isRequired || false}
			errors={control._formState.errors}
			name={name}
		>
			<I18nProvider locale="de-DE-u-ca-gregory">
				<Controller
					control={control}
					name={name}
					render={({ field: { value, onChange } }) => (
						<TimeField
							id={name}
							value={
								value
									? new Time(
											Number(value.split(":")[0]),
											Number(value.split(":")[1]),
											Number(value.split(":")[2] ?? 0),
										)
									: null
							}
							onChange={(time) =>
								onChange(
									time
										? time
												.toString()
												.split(":")
												.slice(0, granularity === "minute" ? 2 : 3)
												.join(":")
										: null,
								)
							}
							className="date-field"
							granularity={granularity}
							hourCycle={24}
							placeholderValue={new Time(12, 0, 0)}
							shouldForceLeadingZeros
						>
							{({ state }) => (
								<div
									onBlur={(event) => {
										if (event.currentTarget.contains(event.relatedTarget)) return;
										const hour = state.segments.find((segment) => segment.type === "hour");
										if (hour?.value == null) return;
										const minute = state.segments.find((segment) => segment.type === "minute");
										const second = state.segments.find((segment) => segment.type === "second");
										const isIncomplete =
											minute?.value == null || (granularity === "second" && second?.value == null);
										if (!isIncomplete) return;
										const time = new Time(
											hour.value,
											minute?.value ?? 0,
											granularity === "second" ? (second?.value ?? 0) : 0,
										);
										onChange(
											time
												.toString()
												.split(":")
												.slice(0, granularity === "minute" ? 2 : 3)
												.join(":"),
										);
									}}
								>
									<DateInput className="date-input">
										{(segment) => (
											<DateSegment
												segment={segment}
												className={cn("date-input-segment")}
											>
												{(item) =>
													item.isPlaceholder ? (
														<span className="date-input-segment-placeholder">
															{getPlaceholderString(item)}
														</span>
													) : (
														item.text
													)
												}
											</DateSegment>
										)}
									</DateInput>
								</div>
							)}
						</TimeField>
					)}
				/>
			</I18nProvider>
		</InputWrapper>
	);
}
